<template>
    <div class="max-w-8xl mx-auto px-4 py-8">
        <h1 class="text-2xl font-bold text-left mb-10">
            {{ translate('buying history', 'ucfirst') }}
        </h1>

        <Tabs v-model:value="activeTab" class="mb-8">
            <TabList>
                <Tab v-for="item, index of tabs" :key="index" :value="item.value">
                    {{ translate(item.title, 'ucfirst') }}
                </Tab>
            </TabList>

            <LoadingDialog :visible="loadingPayments" />
            <TabPanels>

                <div class="hidden md:block">
                    <DataTable :value="paymentsList" responsiveLayout="stack" breakpoint="960px"
                        class="p-datatable-sm udemy-style-table">
                        <Column field="title" :header="translate('item', 'ucfirst')">
                            <template #body="slotProps">
                                <div class="flex items-center">
                                    <div class="p-3 bg-gray-100 rounded mr-4 hidden md:block">
                                        <i class="pi pi-shopping-cart text-gray-600"></i>
                                    </div>
                                    <div>
                                        <template v-if="slotProps.data.items_count > 1">
                                            <span class="text-xs text-gray-500">
                                                + {{ slotProps.data.items_count }} {{ translate('purchased items') }}
                                            </span>
                                            <div>
                                                <a 
                                                    v-for="item, index of slotProps.data.items" 
                                                    :key="index" 
                                                    @click="goToCoursePage(item.uuid)" 
                                                    class="cursor-pointer text-blog-orange hover:underline font-bold block leading-tight"
                                                >
                                                    {{ item.title }}
                                                </a>
                                            </div>

                                        </template>

                                        <template v-if="slotProps.data.items_count == 1">
                                            <a @click="goToCoursePage(slotProps.data?.items[0]?.uuid)" class="cursor-pointer text-blog-orange hover:underline font-bold block leading-tight">
                                                {{ slotProps.data?.items[0]?.title }}
                                            </a>
                                        </template>

                                    </div>
                                </div>
                            </template>
                        </Column>

                        <Column field="date" :header="translate('date', 'ucfirst')" class="text-sm text-gray-600"></Column>

                        <Column :header="translate('total price', 'ucfirst')" class="font-bold text-carrot">
                            <template #body="slotProps">
                                {{ slotProps.data.total }}
                            </template>
                        </Column>

                        <Column :header="translate('payment type', 'ucfirst')" class="text-sm text-gray-600">
                            <template #body="slotProps">
                                {{ translate(slotProps.data.payment_method, 'ucfirst') }}
                            </template>
                        </Column>

                        <Column class="text-right">
                            <template #body="slotProps">
                                <div class="flex flex-col md:flex-row gap-3 justify-center">
                                    <Button v-if="slotProps.data.is_approved" :label="translate('receipt', 'ucfirst')" size="small"
                                        @click="viewReceipt(slotProps.data.payment_id)" class="bg-purple-suit-forced text-white p-1" />
                                    <span v-else class="text-xs text-gray-400 italic my-auto">{{ translate('unavailable', 'ucfirst', 'ucfrist') }}</span>

                                    <Button :label="translate('invoice', 'ucfirst')" size="small" @click="viewInvoice(slotProps.data.payment_id)"
                                        class="no-bg-button text-white p-1" />
                                </div>
                            </template>
                        </Column>

                        <template #empty>
                            <div class="py-10 text-center">
                                <p>{{ translate('no purchases found', 'ucfirst') }}.</p>
                            </div>
                        </template>
                    </DataTable>
                </div>

                <div class="md:hidden space-y-4">
                    <div v-for="payment in paymentsList" :key="payment.payment_id" 
                        class="bg-white border border-iron rounded-lg p-4 shadow-sm">
                        
                        <div class="flex justify-between items-start mb-3">
                            <span class="text-xs font-bold uppercase text-gray-400">{{ payment.date }}</span>
                            <span class="font-bold text-carrot">{{ payment.total }}</span>
                        </div>

                        <div class="mb-4">
                            <p class="text-xs text-gray-500 mb-1" v-if="payment.items_count > 1">
                                + {{ payment.items_count }} {{ translate('purchased items') }}
                            </p>
                            <div class="flex flex-col gap-1">
                                <a v-for="item in payment.items" :key="item.uuid"
                                @click="goToCoursePage(item.uuid)"
                                class="text-blue-700 font-bold text-sm leading-tight cursor-pointer">
                                    {{ item.title }}
                                </a>
                            </div>
                        </div>

                        <div class="flex gap-2 pt-3 border-t border-iron">
                            <Button v-if="payment.is_approved" 
                                :label="translate('receipt', 'ucfirst')"
                                icon="pi pi-file"
                                class="flex-1 bg-purple-suit-forced text-xs p-2" 
                                @click="viewReceipt(payment.payment_id)" />
                            
                            <Button icon="pi pi-receipt"
                                :label="translate('invoice', 'ucfirst')"
                                class="flex-1 no-bg-button text-xs p-2" 
                                @click="viewInvoice(payment.payment_id)" />
                        </div>
                    </div>
                    
                    <div v-if="paymentsList.length === 0" class="text-center py-10 bg-gray-50 rounded">
                        <p>{{ translate('no purchases found', 'ucfirst') }}.</p>
                    </div>
                </div>

            </TabPanels>
        </Tabs>
    </div>
</template>

<script setup>
    import { ref, onMounted, watch } from 'vue';
    import DataTable from 'primevue/datatable';
    import Column from 'primevue/column';
    import Tabs from 'primevue/tabs';
    import TabList from 'primevue/tablist';
    import Tab from 'primevue/tab';
    import TabPanels from 'primevue/tabpanels';
    import Button from 'primevue/button';
    import { translate } from '@/composables/translate';
    import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
    import paymentService from '@/services/PaymentService';
    import urlHandler from '@/composables/urlHandler';
    import { showToast } from '@/composables/toast';
    import { useToast } from 'primevue';

    const tabs = ref([
        { title: 'approved', value: '2' },
        { title: 'pending', value: '1' },
        { title: 'refused', value: '3' },
        { title: 'expired', value: '4' },
    ]);
    const activeTab = ref('2');
    const loadingPayments = ref(true);
    const toast = useToast();

    onMounted(() => {
        getPayments();
    });

    watch(activeTab, (newValue) => {
        if (newValue) {
            getPayments();
        }
    });

    const paymentsList = ref([]);
    async function getPayments() {
        loadingPayments.value = true;

        try {
            const payments = await paymentService.getPaymentsByStatus(activeTab.value);
            paymentsList.value = payments.map(payment => payment.simplifyData());

        } catch (error) {
            paymentsList.value = [];

        } finally {
            loadingPayments.value = false;
        }
    }

    const viewReceipt = (payment_id = '') => {
        paymentService.openMercadoPagoReceiptPage(payment_id);
    };
    const viewInvoice = (payment_id) => {
        urlHandler.redirectAndScroll(`/dashboard/payment_invoice/${payment_id}`);
    }

    function goToCoursePage(courseUuid = null) {
        if (courseUuid) {
            urlHandler.redirectAndScroll(`/course/${courseUuid}`);
        } else {
            showToast(toast, translate('course not found', 'ucfirst'), 'info');
        }
    }
</script>

<style scoped>
    :deep(.p-tablist-tab-list) {
        border-bottom: 1px solid #e5e7eb;
    }

    :deep(.p-tab) {
        padding: 1rem 0;
        margin-right: 2rem;
        color: #6b7280;
        border-bottom: 2px solid transparent;
    }

    :deep(.p-tab-active) {
        color: #000 !important;
        border-color: #000 !important;
        font-weight: 700;
    }

    :deep(.p-datatable-thead > tr > th) {
        background-color: transparent;
        color: #374151;
        font-size: 0.875rem;
        border-bottom: 1px solid #e5e7eb;
    }

    :deep(.p-button-outlined.p-button-secondary) {
        border-color: #1c1d1f;
        color: #1c1d1f;
    }

    :deep(.p-button-outlined.p-button-secondary:hover) {
        background-color: #f7f9fa;
        border-color: #1c1d1f;
    }
</style>